import { useState, useCallback, useRef } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button, Badge } from '@shared/components/ui';
import { useDialog } from '@shared/hooks/useDialog';
import { api } from '@shared/services/api';
import {
  Check,
  Copy,
  Download,
  ShieldCheck,
  AlertTriangle,
  Server,
  Database,
  Folder,
  ArrowRight,
  SkipForward,
  Loader2,
  RefreshCw,
  ExternalLink,
  Plus,
  X,
  Zap,
} from 'lucide-react';
import {
  TemplateSelection,
  type Template,
} from '@features/backups/components/simple-wizard/TemplateSelection';
import {
  BasicDetails,
  type Agent as BasicDetailsAgent,
} from '@features/backups/components/simple-wizard/BasicDetails';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface Agent extends BasicDetailsAgent {
  status: 'online' | 'offline' | 'revoked';
  last_heartbeat?: string;
}

interface WizardData {
  name: string;
  agent_id: string;
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  database?: string;
  path?: string;
  useVSS?: boolean;
}

const INITIAL_DATA: WizardData = {
  name: '',
  agent_id: '',
};

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { showSuccess, showError } = useDialog();

  // Wizard steps: 'create-agent' | 'wait-agent' | 'create-backup' | 'running-backup' | 'complete'
  const [step, setStep] = useState<
    'create-agent' | 'wait-agent' | 'create-backup' | 'running-backup' | 'complete'
  >('create-agent');
  const [currentSubStep, setCurrentSubStep] = useState<1 | 2 | 3>(1);

  // Agent creation state
  const [agentName, setAgentName] = useState('');
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [newAgent, setNewAgent] = useState<{
    id: string;
    name: string;
    token: string;
    download_url?: string;
  } | null>(null);

  // Agent status polling
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isPolling, setIsPolling] = useState(false);

  // Backup creation state
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [backupData, setBackupData] = useState<WizardData>(INITIAL_DATA);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [, setCreatedConfigId] = useState<string | null>(null);

  // Backup execution state. The job id is read inside the adaptive poll's
  // fetcher, so it lives in a ref rather than state (a state dep would restart
  // the timer on every change).
  const backupJobIdRef = useRef<string | null>(null);
  const [backupStatus, setBackupStatus] = useState<'pending' | 'running' | 'completed' | 'failed'>(
    'pending'
  );

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const response = await api.get('/agents');
      const agentsData = response.data || [];
      setAgents(agentsData);
      return agentsData;
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      return [];
    }
  }, []);

  // Create agent
  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentName.trim()) return;

    setCreatingAgent(true);
    try {
      const response = await api.post('/agents', { name: agentName.trim() });
      setNewAgent(response.data);
      // Start polling for agent status
      setIsPolling(true);
      setStep('wait-agent');
    } catch (error: any) {
      const errorMessage =
        error?.userMessage || error?.appError?.userMessage || t('createAgent.error');
      await showError(t('createAgent.errorTitle'), errorMessage);
    } finally {
      setCreatingAgent(false);
    }
  };

  // Poll for agent online status. Adaptive: 5s base backing off to 30s while
  // the agent is still offline, and paused while the tab is hidden.
  useAdaptivePoll({
    enabled: isPolling && !!newAgent,
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      const agentsData = await fetchAgents();
      const createdAgent = agentsData.find((a: Agent) => a.id === newAgent?.id);

      if (createdAgent?.status?.toLowerCase() === 'online') {
        setIsPolling(false);
        setBackupData((prev) => ({ ...prev, agent_id: createdAgent.id }));
        setStep('create-backup');
        setCurrentSubStep(1);
        return 'online';
      }
      return createdAgent?.status ?? 'unknown';
    },
    isEqual: (a, b) => a === b,
  });

  // Handle template selection
  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setCurrentSubStep(2);
  };

  // Handle backup data change
  const handleBackupDataChange = (newData: Record<string, unknown>) => {
    setBackupData((prev) => ({ ...prev, ...newData }));
  };

  // Create backup config and trigger backup
  const handleCreateBackup = async () => {
    if (!selectedTemplate || !backupData.agent_id) return;

    setCreatingBackup(true);
    try {
      // Fetch default PLATFORM storage
      const storageRes = await api.get('/storage');
      const storageData = storageRes.data;
      const destinations = storageData.destinations || storageData;
      const platformStorage = destinations.find((s: { type: string }) => s.type === 'PLATFORM');

      if (!platformStorage) {
        await showError(t('createBackup.noStorageTitle'), t('createBackup.noStorageMessage'));
        setCreatingBackup(false);
        return;
      }

      const payload = {
        name: backupData.name,
        type: selectedTemplate.type,
        agent_id: backupData.agent_id,
        storage_destination_id: platformStorage.id,
        run_missing_jobs_on_startup: false,
        retention_policy: {
          keep_daily: selectedTemplate.retentionDaily,
          keep_weekly: selectedTemplate.retentionWeekly,
          keep_monthly: selectedTemplate.retentionMonthly,
          keep_last: selectedTemplate.retentionLast,
        },
        config:
          selectedTemplate.type === 'POSTGRESQL'
            ? {
                host: backupData.host,
                port: backupData.port,
                username: backupData.username,
                password: backupData.password,
                database: backupData.database,
                databases: backupData.database ? [backupData.database] : [],
                use_pg_dump_all: false,
                backup_globals_only: false,
              }
            : {
                paths: backupData.path ? [backupData.path] : [],
                vss: {
                  enabled: true,
                },
              },
        schedule: {
          type: selectedTemplate.scheduleType === 'CUSTOM' ? 'CRON' : selectedTemplate.scheduleType,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          ...(selectedTemplate.scheduleType === 'CUSTOM' && {
            cron_expression: selectedTemplate.cronExpression,
          }),
        },
      };

      const res = await api.post('/backups/configs', payload);

      if (res.data) {
        const configId = res.data.config?.id || res.data.id;
        setCreatedConfigId(configId);

        // Set alert settings
        try {
          await api.put(`/backups/configs/${configId}/alert-settings`, {
            grace_period_minutes: selectedTemplate.alertGracePeriod,
            throttle_interval_minutes: selectedTemplate.alertThrottle,
          });
        } catch (e) {
          console.error('Failed to set alert settings:', e);
        }

        // Move to running backup step
        setStep('running-backup');

        // Trigger backup immediately
        await triggerBackup(configId);
      }
    } catch (error: any) {
      const errorMessage =
        error?.userMessage || error?.appError?.userMessage || t('createBackup.error');
      await showError(t('createBackup.errorTitle'), errorMessage);
    } finally {
      setCreatingBackup(false);
    }
  };

  // Trigger backup
  const triggerBackup = async (configId: string) => {
    try {
      const response = await api.post('/backup-jobs', {
        backup_config_id: configId,
      });

      if (response.data) {
        backupJobIdRef.current = response.data.id;
        setBackupStatus('running');
        // The adaptive poll above picks this up from the `running` status.
      }
    } catch (error: any) {
      console.error('Failed to trigger backup:', error);
      setBackupStatus('failed');
    }
  };

  // Poll the triggered backup to completion. Adaptive: 5s base backing off to
  // 30s while the status is unchanged, and paused while the tab is hidden. The
  // previous fixed 3s interval had no visibility awareness at all.
  useAdaptivePoll({
    enabled: backupStatus === 'running' && !!backupJobIdRef.current,
    baseIntervalMs: 5000,
    maxIntervalMs: 30000,
    fetcher: async () => {
      const id = backupJobIdRef.current;
      if (!id) return 'idle';
      try {
        const response = await api.get<{ status: string }>(`/backup-jobs/${id}`);
        const status = response.data.status;
        if (status === 'COMPLETED') {
          setBackupStatus('completed');
          setStep('complete');
        } else if (status === 'FAILED') {
          setBackupStatus('failed');
        }
        return status;
      } catch (error) {
        console.error('Failed to fetch backup status:', error);
        return 'error';
      }
    },
    isEqual: (a, b) => a === b,
  });

  // Skip onboarding
  const handleSkip = () => {
    onSkip();
  };

  // Complete onboarding
  const handleComplete = () => {
    onComplete();
  };

  // Copy token to clipboard
  const copyToken = async () => {
    if (newAgent?.token) {
      await navigator.clipboard.writeText(newAgent.token);
      await showSuccess(t('tokenCopied'), t('tokenCopiedDesc'));
    }
  };

  // Render step indicator
  const renderStepIndicator = () => {
    const steps = [
      { id: 'create-agent', label: t('steps.createAgent') },
      { id: 'wait-agent', label: t('steps.connectAgent') },
      { id: 'create-backup', label: t('steps.createBackup') },
      { id: 'running-backup', label: t('steps.runBackup') },
    ];

    const currentStepIndex = steps.findIndex((s) => s.id === step);

    return (
      <div className="flex items-center justify-center space-x-2 mb-8">
        {steps.map((s, index) => (
          <div key={s.id} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                index <= currentStepIndex
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-border-light dark:bg-surface-border-dark text-text-muted-light dark:text-text-muted-dark'
              }`}
            >
              {index < currentStepIndex ? <Check size={16} /> : index + 1}
            </div>
            <span
              className={`ml-2 text-sm font-medium hidden sm:block ${
                index <= currentStepIndex
                  ? 'text-slate-900 dark:text-white'
                  : 'text-text-muted-light dark:text-text-muted-dark'
              }`}
            >
              {s.label}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`w-8 sm:w-12 h-0.5 mx-2 sm:mx-4 ${
                  index < currentStepIndex
                    ? 'bg-brand-600'
                    : 'bg-surface-border-light dark:bg-surface-border-dark'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render create agent step
  const renderCreateAgentStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center p-4 bg-accent-soft-light dark:bg-accent-soft-dark rounded-2xl mb-4">
          <Server size={40} className="text-brand-600 dark:text-brand-400" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
          {t('createAgent.title')}
        </h2>
        <p className="text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto">
          {t('createAgent.description')}
        </p>
      </div>

      <form onSubmit={handleCreateAgent} className="max-w-md mx-auto space-y-4">
        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
            {t('createAgent.agentNameLabel')}
          </label>
          <input
            type="text"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            placeholder={t('createAgent.agentNamePlaceholder')}
            className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            required
          />
        </div>
        <Button
          type="submit"
          className="w-full"
          size="lg"
          isLoading={creatingAgent}
          disabled={!agentName.trim()}
        >
          {creatingAgent ? t('createAgent.creating') : t('createAgent.createButton')}
        </Button>
      </form>

      <div className="text-center">
        <button
          onClick={handleSkip}
          className="text-sm text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 inline-flex items-center"
        >
          <SkipForward size={16} className="mr-2" />
          {t('skipWizard')}
        </button>
      </div>
    </div>
  );

  // Render wait agent step
  const renderWaitAgentStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center p-4 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-2xl mb-4">
          <ShieldCheck size={40} className="text-state-ok-light dark:text-state-ok-dark" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
          {t('waitAgent.title')}
        </h2>
        <p className="text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto">
          {t('waitAgent.description')}
        </p>
      </div>

      {newAgent && (
        <Card className="max-w-lg mx-auto p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark uppercase tracking-wider">
                {t('waitAgent.agentName')}
              </p>
              <p className="text-lg font-black text-slate-900 dark:text-white">{newAgent.name}</p>
            </div>
            <Badge variant="warning">{t('waitAgent.waiting')}</Badge>
          </div>

          <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
            <p className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark mb-2">
              {t('waitAgent.tokenLabel')}
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 text-sm font-mono bg-surface-2-light dark:bg-surface-2-dark px-3 py-2 rounded-lg break-all">
                {newAgent.token}
              </code>
              <Button variant="secondary" size="sm" onClick={copyToken}>
                <Copy size={16} />
              </Button>
            </div>
          </div>

          {newAgent.download_url && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => window.open(newAgent.download_url, '_blank')}
            >
              <Download size={18} className="mr-2" />
              {t('waitAgent.downloadInstaller')}
            </Button>
          )}

          <div className="flex items-center space-x-2 text-state-warn-light dark:text-state-warn-dark bg-state-warn-bg-light dark:bg-state-warn-bg-dark p-3 rounded-lg">
            <AlertTriangle size={18} />
            <p className="text-xs font-bold">{t('waitAgent.tokenWarning')}</p>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-center space-x-2 text-text-subtle-light dark:text-text-subtle-dark">
        <Loader2 size={20} className="animate-spin" />
        <span className="text-sm">{t('waitAgent.polling')}</span>
      </div>

      <div className="text-center">
        <button
          onClick={() => setStep('create-backup')}
          className="text-sm text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 inline-flex items-center"
        >
          {t('waitAgent.continueWithoutAgent')}
          <ArrowRight size={16} className="ml-2" />
        </button>
      </div>
    </div>
  );

  // Render create backup step
  const renderCreateBackupStep = () => {
    const onlineAgents = agents.filter((a) => a.status === 'online');

    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark rounded-2xl mb-4">
            {selectedTemplate?.type === 'POSTGRESQL' ? (
              <Database size={40} className="text-state-info-light dark:text-state-info-dark" />
            ) : (
              <Folder size={40} className="text-state-info-light dark:text-state-info-dark" />
            )}
          </div>
          <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
            {t('createBackup.title')}
          </h2>
          <p className="text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto">
            {t('createBackup.description')}
          </p>
        </div>

        {currentSubStep === 1 && (
          <TemplateSelection
            selectedTemplate={selectedTemplate}
            onSelect={handleTemplateSelect}
            onNext={() => {}}
          />
        )}

        {currentSubStep === 2 && selectedTemplate && (
          <BasicDetails
            template={selectedTemplate}
            data={backupData}
            agents={onlineAgents}
            onChange={handleBackupDataChange}
            onNext={handleCreateBackup}
            onBack={() => setCurrentSubStep(1)}
            isCreating={creatingBackup}
          />
        )}

        <div className="text-center">
          <button
            onClick={handleSkip}
            className="text-sm text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 inline-flex items-center"
          >
            <SkipForward size={16} className="mr-2" />
            {t('skipWizard')}
          </button>
        </div>
      </div>
    );
  };

  // Render running backup step
  const renderRunningBackupStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center p-4 bg-accent-soft-light dark:bg-accent-soft-dark rounded-2xl mb-4">
          <Zap size={40} className="text-accent-text-light dark:text-accent-text-dark" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
          {t('runningBackup.title')}
        </h2>
        <p className="text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto">
          {t('runningBackup.description')}
        </p>
      </div>

      <Card className="max-w-md mx-auto p-8 text-center">
        {backupStatus === 'pending' && (
          <>
            <Loader2
              size={48}
              className="animate-spin mx-auto mb-4 text-brand-600 dark:text-brand-400"
            />
            <p className="text-slate-600 dark:text-slate-300">{t('runningBackup.starting')}</p>
          </>
        )}

        {backupStatus === 'running' && (
          <>
            <Loader2
              size={48}
              className="animate-spin mx-auto mb-4 text-brand-600 dark:text-brand-400"
            />
            <p className="text-slate-600 dark:text-slate-300">{t('runningBackup.inProgress')}</p>
            <div className="mt-4 w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-2">
              <div
                className="bg-brand-600 h-2 rounded-full animate-pulse"
                style={{ width: '60%' }}
              />
            </div>
          </>
        )}

        {backupStatus === 'completed' && (
          <>
            <div className="w-16 h-16 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-state-ok-light dark:text-state-ok-dark" />
            </div>
            <p className="text-slate-900 dark:text-white font-bold">
              {t('runningBackup.completed')}
            </p>
          </>
        )}

        {backupStatus === 'failed' && (
          <>
            <div className="w-16 h-16 bg-state-danger-bg-light dark:bg-state-danger-bg-dark rounded-full flex items-center justify-center mx-auto mb-4">
              <X size={32} className="text-state-danger-light dark:text-state-danger-dark" />
            </div>
            <p className="text-slate-900 dark:text-white font-bold">{t('runningBackup.failed')}</p>
          </>
        )}
      </Card>

      {(backupStatus === 'completed' || backupStatus === 'failed') && (
        <div className="flex justify-center space-x-4">
          <Button variant="secondary" onClick={() => navigate('/backup-jobs')}>
            <ExternalLink size={18} className="mr-2" />
            {t('runningBackup.viewJobs')}
          </Button>
          <Button onClick={handleComplete}>
            <Check size={18} className="mr-2" />
            {t('runningBackup.finish')}
          </Button>
        </div>
      )}
    </div>
  );

  // Render complete step
  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-20 h-20 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={40} className="text-state-ok-light dark:text-state-ok-dark" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
          {t('complete.title')}
        </h2>
        <p className="text-text-muted-light dark:text-text-muted-dark max-w-md mx-auto">
          {t('complete.description')}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Button variant="secondary" onClick={handleComplete}>
          <RefreshCw size={18} className="mr-2" />
          {t('complete.goToDashboard')}
        </Button>
        <Button onClick={() => navigate('/backups/quick')}>
          <Plus size={18} className="mr-2" />
          {t('complete.createAnother')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-2-light dark:bg-surface-2-dark py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-2">
            {t('welcomeTitle')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark">{t('welcomeSubtitle')}</p>
        </div>

        {/* Step Indicator */}
        {step !== 'complete' && renderStepIndicator()}

        {/* Content */}
        <Card className="p-6 sm:p-8">
          {step === 'create-agent' && renderCreateAgentStep()}
          {step === 'wait-agent' && renderWaitAgentStep()}
          {step === 'create-backup' && renderCreateBackupStep()}
          {step === 'running-backup' && renderRunningBackupStep()}
          {step === 'complete' && renderCompleteStep()}
        </Card>
      </div>
    </div>
  );
}
